import getErrorMessage from 'zr-utilities/getErrorMessage';
import apiRequest from 'zr-utilities/api';
import logJsUnexpected from 'zr-utilities/telemetry/logJsUnexpected';
import { NextActionResponse } from './nextActionTypes';

const API_URL = '/apply/api/v2/next-action';

interface InputProps {
  encryptedQuizId: string,
  extraSearchParams: Record<string, string>,
  listingVersionKey: string,
  encryptedResponseId?: string,
}

/**
 * Calls the apply next-action endpoint to get the next apply action or a finished apply status
 *
 * @param {string} options.encryptedQuizId quiz id of the apply, either quiz id or response id is required
 * @param {object} options.extraSearchParams any extra search params that should be sent
 * @param {string} options.listingVersionKey listing version key of the apply
 * @param {string} options.encryptedResponseId response id of the apply, not always available
 * @returns json response
 */
export async function fetchNextAction({
  encryptedQuizId,
  listingVersionKey,
  encryptedResponseId,
  extraSearchParams,
}: InputProps): Promise<NextActionResponse> {
  // guard clauses and error handling because places where this function is implemented may not have type safety
  let errorMessage;
  if (!encryptedQuizId && !encryptedResponseId) {
    errorMessage = 'One of encryptedQuizId or responseId is required.';
  }

  // We want every Application to have a listing version key. Uncomment this block when we are confident it is
  // implemented
  // if (!listingVersionKey) {
  //   errorMessage = 'A Listing Version Key is required for all applications.';
  // }

  if (errorMessage) {
    logJsUnexpected(errorMessage);
    throw new Error(errorMessage);
  }

  const searchParams = { ...extraSearchParams };
  if (listingVersionKey) { searchParams.listing_key = listingVersionKey; }
  if (encryptedQuizId) { searchParams.quiz_id = encryptedQuizId; }
  if (encryptedResponseId) { searchParams.response_id = encryptedResponseId; }

  const apiUrl = `${API_URL}?${(new URLSearchParams(searchParams)).toString()}`;

  const fetchInit = {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  };

  try {
    const response = await apiRequest(apiUrl, fetchInit, undefined, true);
    return (await response.json()) as NextActionResponse;
  } catch (e) {
    logJsUnexpected(getErrorMessage(e));
    throw e;
  }
}

export default fetchNextAction;
